import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { ArrowRight, ArrowUpRight, Search, ChevronLeft, ChevronRight, Heart, Users, BadgeCheck, ShieldCheck } from 'lucide-react';
import cloudsBg from '../../assets/images/clouds.png';
import profileDefaultCover from '../../assets/images/clouds2.png';
import BriefCard, { type BriefCardData } from '../../components/Briefs/BriefCard';
import BriefDetailModal, { BriefDetailType } from '../../components/Profile/brief/BriefDetailModal';
import WorkDetailModal, { WorkDetail } from '../../components/Profile/work/WorkDetailModal';
import { discoverService } from '../../services/discoverService';
import type { Brief as DiscoverBrief, Creator } from '../../types/discover';
import { DEFAULT_AVATAR_URL } from '../../constants/assets';
import WorkCard from '../../components/UI/WorkCard';
import { postService, type WorkSummary } from '../../services/postService';
import { userService } from '../../services/userService';
import type { UserProfile } from '../../types/api';

const parseMetricValue = (value?: string | number): number => {
  if (typeof value === 'number') return value;
  if (!value) return 0;

  const normalized = String(value).replace(/,/g, '').trim().toLowerCase();
  const match = normalized.match(/([\d.]+)/);
  if (!match) return 0;

  const base = Number(match[1]);
  if (Number.isNaN(base)) return 0;
  if (normalized.includes('万') || normalized.includes('w')) return Math.round(base * 10000);
  if (normalized.includes('k')) return Math.round(base * 1000);
  return Math.round(base);
};

const formatMetricValue = (value?: string | number): string => {
  const numericValue = parseMetricValue(value);
  if (numericValue >= 10000) return `${(numericValue / 10000).toFixed(numericValue >= 100000 ? 0 : 1)}万`;
  if (numericValue >= 1000) return `${(numericValue / 1000).toFixed(1)}k`;
  return String(numericValue);
};

const sortCreatorsByFollowers = (creators: Creator[]): Creator[] => {
  return [...creators].sort((a, b) => {
    const followerDiff = parseMetricValue(b.followers) - parseMetricValue(a.followers);
    if (followerDiff !== 0) return followerDiff;
    return parseMetricValue(b.likes) - parseMetricValue(a.likes);
  });
};

const mapProfileToHeroCreator = async (profile: UserProfile): Promise<Creator> => {
  const [followersRes, statsRes] = await Promise.all([
    userService.getFollowers(profile.userId, { suppressAuthPrompt: true }),
    userService.getUserStats(profile.userId),
  ]);

  return {
    id: profile.userId,
    name: profile.displayName || profile.username || profile.userId,
    handle: profile.username || profile.userId,
    avatar: profile.avatarUrl || DEFAULT_AVATAR_URL,
    cover: profile.coverImageUrl || '',
    tags: profile.creatorInfo?.tags || [],
    followers: String(followersRes.followers?.length || 0),
    likes: String(statsRes.success ? statsRes.data?.totalLikes || 0 : 0),
    rating: statsRes.success ? statsRes.data?.averageRating || 0 : 0,
    reviewCount: statsRes.success ? statsRes.data?.reviewCount || 0 : 0,
    joinDate: profile.createdAt ? new Date(profile.createdAt).toISOString().slice(0, 10) : '',
    verified: profile.verified,
    isOfficial: profile.isOfficial,
    enterpriseVerified: Boolean((profile as UserProfile & { enterpriseVerified?: boolean }).enterpriseVerified),
    type: profile.accountType === 'company' ? 'studio' : 'personal',
    pinnedWorks: [],
  };
};

const mergeProfileIntoHeroCreator = (creator: Creator, profile?: UserProfile | null): Creator => {
  if (!profile) return creator;

  return {
    ...creator,
    name: profile.displayName || profile.username || creator.name,
    handle: profile.username || creator.handle,
    avatar: profile.avatarUrl || creator.avatar || DEFAULT_AVATAR_URL,
    cover: profile.coverImageUrl || '',
    tags: profile.creatorInfo?.tags?.length ? profile.creatorInfo.tags : creator.tags,
    verified: Boolean(profile.verified),
    isOfficial: Boolean(profile.isOfficial),
    enterpriseVerified: Boolean((profile as UserProfile & { enterpriseVerified?: boolean }).enterpriseVerified ?? creator.enterpriseVerified),
    type: profile.accountType === 'company' ? 'studio' : 'personal',
  };
};

const enrichHeroCreatorsWithProfiles = async (creators: Creator[]): Promise<Creator[]> => {
  const rankedCreators = sortCreatorsByFollowers(creators);
  const topCreatorIds = rankedCreators.slice(0, 5).map((creator) => creator.id);
  const profileEntries = await Promise.all(
    topCreatorIds.map(async (creatorId) => {
      try {
        const response = await userService.getUserProfileById(creatorId);
        return [creatorId, response.success ? response.data?.userProfile || null : null] as const;
      } catch (error) {
        console.error('Failed to load hero creator profile:', creatorId, error);
        return [creatorId, null] as const;
      }
    })
  );
  const profilesById = new Map(profileEntries);

  return creators.map((creator) => mergeProfileIntoHeroCreator(creator, profilesById.get(creator.id)));
};

const getHeroCreators = async (): Promise<Creator[]> => {
  const [creators, profileRes] = await Promise.all([
    discoverService.getCreators({ limit: 100 }),
    userService.getUserProfile({ suppressAuthPrompt: true }),
  ]);

  const currentProfile = profileRes.success ? profileRes.data?.userProfile : null;
  if (!currentProfile) return enrichHeroCreatorsWithProfiles(creators);

  const currentUserInList = creators.some((creator) => creator.id === currentProfile.userId);
  if (currentUserInList) return enrichHeroCreatorsWithProfiles(creators);

  const currentUserCreator = await mapProfileToHeroCreator(currentProfile);
  return enrichHeroCreatorsWithProfiles([currentUserCreator, ...creators]);
};

const getHeroCreatorCover = (creator: Creator) => {
  return creator.cover || profileDefaultCover;
};

const HERO_RANK_TONES = [
  {
    badge: 'bg-[oklch(0.88_0.10_82)] text-[oklch(0.38_0.08_64)]',
    cover: 'bg-[radial-gradient(circle_at_24%_10%,oklch(0.99_0.02_88)_0_18%,transparent_19%),linear-gradient(135deg,oklch(0.60_0.19_22),oklch(0.56_0.21_12))]',
  },
  {
    badge: 'bg-[oklch(0.88_0.04_250)] text-[oklch(0.37_0.06_255)]',
    cover: 'bg-[linear-gradient(135deg,oklch(0.77_0.08_246),oklch(0.90_0.08_62))]',
  },
  {
    badge: 'bg-[oklch(0.87_0.08_45)] text-[oklch(0.42_0.10_38)]',
    cover: 'bg-[linear-gradient(135deg,oklch(0.35_0.09_28),oklch(0.70_0.15_54))]',
  },
  {
    badge: 'bg-[oklch(0.87_0.06_274)] text-[oklch(0.39_0.09_276)]',
    cover: 'bg-[linear-gradient(135deg,oklch(0.50_0.08_228),oklch(0.86_0.05_218))]',
  },
  {
    badge: 'bg-[oklch(0.88_0.08_150)] text-[oklch(0.38_0.10_150)]',
    cover: 'bg-[linear-gradient(135deg,oklch(0.95_0.03_60),oklch(0.86_0.07_334))]',
  },
];

const HeroCreatorRankCard = ({
  creator,
  rank,
  featured = false,
  compact = false,
  className = '',
}: {
  creator: Creator;
  rank: number;
  featured?: boolean;
  compact?: boolean;
  className?: string;
}) => {
  const coverUrl = getHeroCreatorCover(creator);
  const visibleTags = (creator.tags || []).filter(Boolean).slice(0, 2);
  const tone = HERO_RANK_TONES[(rank - 1) % HERO_RANK_TONES.length];
  const minHeightClass = featured
    ? 'min-h-[302px] [@media(max-height:800px)]:min-h-[250px]'
    : compact
      ? 'min-h-[198px] [@media(max-height:800px)]:min-h-[168px]'
      : 'min-h-[236px] [@media(max-height:800px)]:min-h-[198px]';
  const coverHeightClass = featured
    ? 'h-[150px] [@media(max-height:800px)]:h-[112px]'
    : compact
      ? 'h-[76px] [@media(max-height:800px)]:h-[62px]'
      : 'h-[104px] [@media(max-height:800px)]:h-[86px]';
  const bodyClass = featured
    ? 'px-7 pb-7 pt-12 [@media(max-height:800px)]:px-5 [@media(max-height:800px)]:pb-5 [@media(max-height:800px)]:pt-10'
    : compact
      ? 'px-4 pb-3.5 pt-7 [@media(max-height:800px)]:pt-6'
      : 'px-4 pb-4 pt-7 [@media(max-height:800px)]:pt-6';
  const avatarClass = featured
    ? '-top-[72px] left-7 h-36 w-36 [@media(max-height:800px)]:-top-14 [@media(max-height:800px)]:left-5 [@media(max-height:800px)]:h-[108px] [@media(max-height:800px)]:w-[108px]'
    : compact
      ? '-top-8 left-4 h-16 w-16 [@media(max-height:800px)]:-top-7 [@media(max-height:800px)]:h-14 [@media(max-height:800px)]:w-14'
      : '-top-12 left-4 h-24 w-24 [@media(max-height:800px)]:-top-10 [@media(max-height:800px)]:h-20 [@media(max-height:800px)]:w-20';
  const avatarBadgeClass = featured
    ? 'bottom-2 right-2 h-8 w-8 border-[4px] [@media(max-height:800px)]:bottom-1 [@media(max-height:800px)]:right-1 [@media(max-height:800px)]:h-7 [@media(max-height:800px)]:w-7'
    : compact
      ? '-bottom-0.5 -right-0.5 h-5 w-5 border-[3px] [@media(max-height:800px)]:h-[18px] [@media(max-height:800px)]:w-[18px]'
      : 'bottom-0 right-0 h-6 w-6 border-[4px] [@media(max-height:800px)]:h-5 [@media(max-height:800px)]:w-5';
  const avatarBadgeIconClass = featured
    ? 'h-4 w-4 [@media(max-height:800px)]:h-3 [@media(max-height:800px)]:w-3'
    : compact
      ? 'h-2.5 w-2.5'
      : 'h-3 w-3 [@media(max-height:800px)]:h-2.5 [@media(max-height:800px)]:w-2.5';
  const avatarOfficialBadgeTextClass = featured
    ? 'text-sm [@media(max-height:800px)]:text-xs'
    : compact
      ? 'text-[9px]'
      : 'text-[10px] [@media(max-height:800px)]:text-[9px]';
  const contentInsetClass = featured
    ? 'pl-[168px] [@media(max-height:800px)]:pl-[124px]'
    : compact
      ? 'pl-[72px] [@media(max-height:800px)]:pl-[64px]'
      : 'pl-[100px] [@media(max-height:800px)]:pl-[86px]';
  const tagAreaClass = featured
    ? 'mt-6 min-h-[34px]'
    : compact
      ? 'mt-4 min-h-[30px] [@media(max-height:800px)]:mt-3 [@media(max-height:800px)]:min-h-[26px]'
      : 'mt-5 min-h-[32px] [@media(max-height:800px)]:mt-4 [@media(max-height:800px)]:min-h-[28px]';
  const showAvatarVerifiedBadge = !creator.isOfficial && Boolean(creator.enterpriseVerified || creator.verified);

  return (
    <div className={className}>
      <Link
        to={`/profile/${creator.id}`}
        className={`group relative isolate block h-full overflow-hidden rounded-[1.55rem] border border-[oklch(0.96_0.004_250_/_0.86)] bg-[oklch(0.995_0.003_95_/_0.96)] shadow-[0_24px_56px_oklch(0.25_0.025_250_/_0.12)] transition-all duration-300 ease-out before:pointer-events-none before:absolute before:inset-0 before:z-0 before:rounded-[inherit] before:bg-[radial-gradient(circle_at_34%_26%,oklch(0.86_0.14_82_/_0.32),transparent_58%)] before:opacity-0 before:transition-opacity before:duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_76px_oklch(0.64_0.11_82_/_0.24)] hover:before:opacity-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-[oklch(0.45_0.06_250_/_0.20)] focus-visible:ring-offset-2 ${minHeightClass}`}
      >
        <div className={`relative z-10 ${coverHeightClass} overflow-hidden ${tone.cover}`}>
          {coverUrl && (
            <img
              src={coverUrl}
              alt={`${creator.name} 封面`}
              className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
            />
          )}
          <div className="absolute inset-0 bg-[linear-gradient(180deg,oklch(0.12_0.03_250_/_0.02),oklch(0.12_0.03_250_/_0.24))]" />
          <span className={`absolute left-4 top-4 rounded-lg px-2.5 py-1 text-xs font-bold shadow-[0_8px_22px_oklch(0.22_0.03_250_/_0.10)] ${tone.badge}`}>
            TOP {rank}
          </span>
        </div>

        <div className={`relative z-10 ${bodyClass}`}>
          <div className={`absolute rounded-full ${avatarClass}`}>
            <img
              src={creator.avatar || DEFAULT_AVATAR_URL}
              alt={creator.name}
              className="h-full w-full rounded-full border-[3px] border-[oklch(0.995_0.003_95)] object-cover shadow-[0_12px_26px_oklch(0.22_0.03_250_/_0.18)] transition-transform duration-300 group-hover:scale-105"
            />
            {creator.isOfficial ? (
              <span
                className={`absolute flex items-center justify-center rounded-full border-[oklch(0.995_0.003_95)] bg-[oklch(0.74_0.15_82)] font-bold leading-none text-[oklch(0.995_0.003_95)] shadow-sm ${avatarBadgeClass} ${avatarOfficialBadgeTextClass}`}
                title="官方用户"
                aria-label="官方用户"
              >
                官
              </span>
            ) : showAvatarVerifiedBadge && (
              <span
                className={`absolute flex items-center justify-center rounded-full border-[oklch(0.995_0.003_95)] text-[oklch(0.995_0.003_95)] shadow-sm ${avatarBadgeClass} ${
                  creator.enterpriseVerified ? 'bg-[oklch(0.74_0.15_82)]' : 'bg-[oklch(0.62_0.18_255)]'
                }`}
                title={creator.enterpriseVerified ? '企业认证' : '实名认证'}
                aria-label={creator.enterpriseVerified ? '企业认证' : '实名认证'}
              >
                {creator.enterpriseVerified ? (
                  <ShieldCheck className={avatarBadgeIconClass} />
                ) : (
                  <BadgeCheck className={avatarBadgeIconClass} />
                )}
              </span>
            )}
          </div>

          <div className={`relative z-10 flex min-w-0 items-center gap-2 ${contentInsetClass}`}>
            <h3 className={`${featured ? 'text-xl [@media(max-height:800px)]:text-lg' : compact ? 'text-sm [@media(max-height:800px)]:text-[13px]' : 'text-[15px] [@media(max-height:800px)]:text-sm'} min-w-0 truncate font-bold tracking-tight text-[oklch(0.18_0.03_250)] transition-colors group-hover:text-[oklch(0.40_0.09_255)]`}>
              {creator.name}
            </h3>
          </div>

          <div className={`relative z-10 ${compact ? 'mt-2 text-xs' : 'mt-3 text-[13px]'} flex items-center gap-3 font-semibold text-[oklch(0.35_0.025_250)] ${contentInsetClass}`}>
            <span className="inline-flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" />
              {formatMetricValue(creator.followers)}
            </span>
            <span className="h-3 w-px bg-[oklch(0.78_0.006_250)]" />
            <span className="inline-flex items-center gap-1.5">
              <Heart className="h-3.5 w-3.5" />
              {formatMetricValue(creator.likes)}
            </span>
          </div>

          <div className={`relative z-10 flex max-w-full flex-nowrap items-center gap-2 overflow-hidden ${tagAreaClass}`}>
            {visibleTags.length > 0 && (
              <>
              {visibleTags.map((tag) => (
                <span
                  key={tag}
                  className="min-w-0 max-w-[96px] truncate rounded-lg border border-[oklch(0.91_0.006_250)] bg-[oklch(0.97_0.003_250)] px-3 py-1.5 text-xs font-semibold text-[oklch(0.40_0.03_250)]"
                >
                  {tag}
                </span>
              ))}
              </>
            )}
          </div>

          <div className={`pointer-events-none absolute z-0 font-black leading-none text-[oklch(0.90_0.05_82_/_0.26)] ${
            featured
              ? 'bottom-4 right-6 text-[86px]'
              : compact
                ? 'bottom-3 left-4 text-[56px] [@media(max-height:800px)]:text-5xl'
                : 'bottom-3 left-5 text-[64px] [@media(max-height:800px)]:text-[56px]'
          }`}>
            {rank}
          </div>
        </div>
      </Link>
    </div>
  );
};

const HeroSection = ({ creators, loading }: { creators: Creator[]; loading: boolean }) => {
  const heroCreators = sortCreatorsByFollowers(creators).slice(0, 5);

  return (
    <div className="w-full pt-16 sm:pt-20 pb-0 px-3 sm:px-4 md:px-6">
      {/* Container with background. On mobile we collapse the height to
          auto since the right-column illustration is hidden, otherwise
          left content floats in a tall dead space. */}
      <div className="relative w-full h-auto lg:h-[calc(100vh-100px)] lg:min-h-[540px] lg:max-h-[800px] rounded-[1.5rem] sm:rounded-[2.5rem] md:rounded-[4rem] overflow-hidden bg-white flex items-center py-10 sm:py-8 lg:py-8 [@media(max-height:800px)]:lg:py-4">

        {/* Background Image & Gradients */}
        <div className="absolute inset-0 z-0">
          <img
            src={cloudsBg}
            alt="Clouds"
            className="w-full h-full object-cover object-top opacity-90"
            onError={(e) => {
              const target = e.currentTarget;
              if (target.src !== "https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?q=80&w=2000&auto=format&fit=crop") {
                target.src = "https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?q=80&w=2000&auto=format&fit=crop";
              }
            }}
          />
          {/* Subtle overlay gradients for text readability and depth */}
          <div className="absolute inset-0 bg-gradient-to-r from-white/50 to-transparent"></div>
          {/* Fade out to solid white at the bottom for a seamless transition */}
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/60 to-transparent"></div>
          {/* Aggressive fade specifically at the bottom edge to hide container corners */}
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-white via-white/90 to-transparent"></div>
        </div>

        {/* Content Grid */}
        <div className="relative z-10 w-full max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-8 px-5 sm:px-8 md:px-16 h-full items-center py-4">

          {/* Left Column: Typography & CTAs */}
          <div className="flex flex-col justify-center items-start text-left h-full pt-4">

            {/* Top Pill Tag & Platform Name */}
            <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6 [@media(max-height:800px)]:mb-3 animate-fade-in-up">
              <span className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight transition-all duration-700 hover:drop-shadow-[0_0_20px_rgba(253,224,71,0.6)] hover:text-gray-800 cursor-default">云作</span>
              <div className="inline-flex items-center px-3 py-1 rounded-full border border-gray-300 text-xs font-medium text-gray-600 bg-white/40 backdrop-blur-md shadow-sm transition-colors duration-300 hover:bg-white hover:text-gray-900 hover:border-gray-400 cursor-default">
                AIGC 创作外包平台
              </div>
            </div>

            {/* Headline */}
            <h1 className="text-[44px] leading-[1.05] sm:text-5xl sm:leading-[1.1] md:text-[80px] lg:text-[100px] [@media(max-height:800px)]:lg:text-[72px] [@media(max-height:800px)]:md:text-[64px] font-bold text-gray-900 tracking-tight mb-4 sm:mb-6 [@media(max-height:800px)]:mb-4 animate-fade-in-up delay-100 transition-all duration-700 hover:drop-shadow-[0_0_35px_rgba(253,224,71,0.5)] hover:text-gray-800 cursor-default">
              找到最合适的<br />
              AI 创作者
            </h1>

            {/* Subtitle */}
            <p className="text-sm sm:text-base md:text-lg [@media(max-height:800px)]:text-base text-gray-700 font-normal max-w-[420px] leading-relaxed mb-6 sm:mb-10 [@media(max-height:800px)]:mb-6 animate-fade-in-up delay-200 transition-all duration-700 hover:drop-shadow-[0_0_15px_rgba(253,224,71,0.4)] hover:text-gray-800 cursor-default">
              浏览真实作品，比较创作者口碑，把广告片、概念设计、动画和脚本需求推进到可执行的合作。
            </p>

            {/* Buttons — stack vertically on phones to keep tap targets
                comfortable; horizontal from sm: up. */}
            <div className="animate-fade-in-up delay-300 mb-6 sm:mb-10 [@media(max-height:800px)]:mb-6 w-full">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-5 [@media(max-height:800px)]:gap-4 w-full sm:w-auto items-stretch sm:items-center">
                <div className="animate-float w-full sm:w-auto">
                  <Link to="/discover?tab=creators" className="h-14 sm:h-16 md:h-20 [@media(max-height:800px)]:md:h-16 px-6 sm:px-10 md:px-12 [@media(max-height:800px)]:md:px-8 rounded-full bg-gray-900 text-white text-base sm:text-lg md:text-xl [@media(max-height:800px)]:md:text-lg font-medium transition-all duration-700 ease-out hover:scale-[1.03] hover:-translate-y-2 hover:shadow-[0_15px_35px_rgba(0,0,0,0.25)] flex items-center justify-center gap-2 sm:gap-3 shadow-xl shadow-black/10 focus:outline-none focus-visible:ring-4 focus-visible:ring-gray-900/20 focus-visible:ring-offset-2 w-full sm:w-auto">
                    找创作者
                    <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 [@media(max-height:800px)]:w-5 [@media(max-height:800px)]:h-5" />
                  </Link>
                </div>
                <div className="animate-float-delayed w-full sm:w-auto">
                  <Link to="/discover?tab=briefs" className="h-14 sm:h-16 md:h-20 [@media(max-height:800px)]:md:h-16 px-6 sm:px-10 md:px-12 [@media(max-height:800px)]:md:px-8 rounded-full bg-white/80 backdrop-blur-md border border-gray-200 text-gray-900 text-base sm:text-lg md:text-xl [@media(max-height:800px)]:md:text-lg font-medium transition-all duration-700 ease-out hover:scale-[1.03] hover:-translate-y-2 hover:shadow-[0_15px_35px_rgba(0,0,0,0.1)] flex items-center justify-center gap-2 sm:gap-3 shadow-sm focus:outline-none focus-visible:ring-4 focus-visible:ring-gray-900/20 focus-visible:ring-offset-2 w-full sm:w-auto">
                    <Search className="w-5 h-5 sm:w-6 sm:h-6 [@media(max-height:800px)]:w-5 [@media(max-height:800px)]:h-5" />
                    浏览需求
                  </Link>
                </div>
              </div>
            </div>

            {/* Features list at bottom left. Wraps on small screens since
                4 chips don't fit a phone width even with reduced gap. */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 sm:gap-6 md:gap-10 text-xs sm:text-sm font-medium text-gray-500 animate-fade-in-up delay-500">
              <span className="flex items-center gap-2 group cursor-default transition-colors duration-300 hover:text-blue-600"><div className="w-1.5 h-1.5 rounded-full bg-gray-300 transition-colors duration-300 group-hover:bg-blue-500" />多维度筛选</span>
              <span className="flex items-center gap-2 group cursor-default transition-colors duration-300 hover:text-blue-600"><div className="w-1.5 h-1.5 rounded-full bg-gray-300 transition-colors duration-300 group-hover:bg-blue-500" />先看过往作品</span>
              <span className="flex items-center gap-2 group cursor-default transition-colors duration-300 hover:text-blue-600"><div className="w-1.5 h-1.5 rounded-full bg-gray-300 transition-colors duration-300 group-hover:bg-blue-500" />ai梳理需求</span>
              <span className="flex items-center gap-2 group cursor-default transition-colors duration-300 hover:text-blue-600"><div className="w-1.5 h-1.5 rounded-full bg-gray-300 transition-colors duration-300 group-hover:bg-blue-500" />系统保障交付</span>

            </div>
          </div>

          {/* Right Column: ranked creators */}
          <div className="hidden lg:flex relative h-[90%] max-h-[640px] [@media(max-height:800px)]:max-h-[500px] w-full items-center justify-end pr-4">
            {loading ? (
              <div className="flex w-full max-w-[720px] flex-col items-end gap-5 [@media(max-height:800px)]:gap-4">
                <div className="flex w-full translate-x-8 items-end justify-end gap-5 [@media(max-height:800px)]:translate-x-6 [@media(max-height:800px)]:gap-4">
                  <div className="h-[302px] w-[386px] animate-pulse rounded-[1.55rem] bg-white/75 shadow-[0_24px_56px_oklch(0.25_0.025_250_/_0.09)] [@media(max-height:800px)]:h-[250px] [@media(max-height:800px)]:w-[342px]" />
                  <div className="h-[236px] w-[214px] animate-pulse rounded-[1.55rem] bg-white/75 shadow-[0_24px_56px_oklch(0.25_0.025_250_/_0.09)] [@media(max-height:800px)]:h-[198px] [@media(max-height:800px)]:w-[190px]" />
                </div>
                <div className="mr-3 grid w-[670px] grid-cols-3 gap-5 self-start [@media(max-height:800px)]:w-[590px] [@media(max-height:800px)]:gap-4">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div key={`hero-rank-skeleton-${index}`} className="h-[198px] animate-pulse rounded-[1.55rem] bg-white/75 shadow-[0_24px_56px_oklch(0.25_0.025_250_/_0.09)] [@media(max-height:800px)]:h-[168px]" />
                  ))}
                </div>
              </div>
            ) : heroCreators.length === 0 ? (
              <div className="ml-auto flex h-[320px] w-[440px] items-center justify-center rounded-[1.55rem] border border-dashed border-white/80 bg-white/70 text-sm font-medium text-gray-400 shadow-[0_20px_50px_rgba(0,0,0,0.04)]">
                暂无创作者
              </div>
            ) : (
              <div className="flex w-full max-w-[720px] flex-col items-end gap-5 [@media(max-height:800px)]:gap-4">
                <div className="flex w-full translate-x-8 items-end justify-end gap-5 [@media(max-height:800px)]:translate-x-6 [@media(max-height:800px)]:gap-4">
                  {heroCreators[0] && (
                    <HeroCreatorRankCard creator={heroCreators[0]} rank={1} featured className="w-[386px] animate-fade-in-up delay-300 [@media(max-height:800px)]:w-[342px]" />
                  )}
                  {heroCreators[1] && (
                    <HeroCreatorRankCard creator={heroCreators[1]} rank={2} className="w-[214px] animate-fade-in-up delay-400 [@media(max-height:800px)]:w-[190px]" />
                  )}
                </div>
                <div className="mr-3 grid w-[670px] grid-cols-3 gap-5 self-start [@media(max-height:800px)]:w-[590px] [@media(max-height:800px)]:gap-4">
                  {heroCreators.slice(2, 5).map((creator, index) => (
                    <HeroCreatorRankCard key={creator.id} creator={creator} rank={index + 3} compact className="animate-fade-in-up delay-500" />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const RecommendedWorksSection = ({
  works,
  loading,
  onWorkClick,
}: {
  works: WorkSummary[];
  loading: boolean;
  onWorkClick: (work: WorkSummary) => void;
}) => {
  const [globalPage, setGlobalPage] = useState(0);
  const itemsPerPage = 20; // 5 cols * 4 rows on wide desktop

  const totalGlobalPages = Math.max(1, Math.ceil(works.length / itemsPerPage));
  const nextGlobalPage = () => setGlobalPage(p => (p + 1) % totalGlobalPages);
  const prevGlobalPage = () => setGlobalPage(p => (p - 1 + totalGlobalPages) % totalGlobalPages);
  const handleWheel = (e: React.WheelEvent) => {
    if (Math.abs(e.deltaX) > 50) { if (e.deltaX > 0) nextGlobalPage(); else prevGlobalPage(); }
  };

  const gridWorks = works.slice(globalPage * itemsPerPage, (globalPage + 1) * itemsPerPage);

  return (
    <section className="pt-4 sm:pt-6 pb-10 sm:pb-20 px-3 sm:px-4 md:px-6 max-w-[1504px] mx-auto group/section relative" onWheel={handleWheel}>
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 px-2 gap-4">
        <h2 className="text-4xl md:text-5xl font-medium text-gray-900 tracking-tight">
          作品
        </h2>
      </div>
      <div className="relative">
        {totalGlobalPages > 1 && (
          <>
            <button onClick={prevGlobalPage} className="hidden md:flex absolute -left-16 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.08)] items-center justify-center opacity-0 group-hover/section:opacity-100 transition-all duration-300 ease-out hover:scale-110 active:scale-95 border border-gray-100">
              <ChevronLeft className="w-6 h-6 text-gray-800" />
            </button>
            <button onClick={nextGlobalPage} className="hidden md:flex absolute -right-16 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.08)] items-center justify-center opacity-0 group-hover/section:opacity-100 transition-all duration-300 ease-out hover:scale-110 active:scale-95 border border-gray-100">
              <ChevronRight className="w-6 h-6 text-gray-800" />
            </button>
          </>
        )}
        <div className="grid grid-cols-[minmax(0,288px)] justify-center gap-x-4 gap-y-8 transition-all duration-500 min-[640px]:grid-cols-[repeat(2,288px)] min-[960px]:grid-cols-[repeat(3,288px)] min-[1240px]:grid-cols-[repeat(4,288px)] min-[1520px]:grid-cols-[repeat(5,288px)]">
          {loading ? (
            Array.from({ length: itemsPerPage }).map((_, index) => (
              <div
                key={`work-skeleton-${index}`}
                className="animate-pulse overflow-hidden rounded-[1.25rem] border border-[oklch(0.91_0.006_250)] bg-[oklch(0.995_0.003_95)] shadow-[0_10px_28px_oklch(0.22_0.02_250_/_0.07)]"
              >
                <div className="aspect-[4/3] bg-gray-100" />
                <div className="px-4 pb-1.5 pt-1.5">
                  <div className="mb-1 h-4 w-5/6 rounded-full bg-gray-100" />
                  <div className="mb-1 h-3 w-3/4 rounded-full bg-gray-100" />
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-5 rounded-full bg-gray-100" />
                    <div className="h-3 w-20 rounded-full bg-gray-100" />
                  </div>
                </div>
              </div>
            ))
          ) : gridWorks.length === 0 ? (
            <div className="col-span-2 rounded-2xl border border-dashed border-gray-200 py-16 text-center text-sm text-gray-400 md:col-span-5">
              暂无作品
            </div>
          ) : gridWorks.map(work => (
            <WorkCard
              key={work.id}
              work={work}
              variant="home"
              onClick={() => onWorkClick(work)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const AuthorCard = ({ creator, onWorkClick }: { creator: Creator, onWorkClick?: (workId: string, creator: Creator) => void }) => {
  return (
    <Link to={`/profile/${creator.id}`} className="group block bg-white rounded-[2rem] border border-gray-100 p-6 hover:shadow-[0_20px_50px_rgba(0,0,0,0.06)] transition-all duration-500 ease-out hover:-translate-y-1 hover:border-gray-200">
      {/* Top section: Avatar, Info, Stats */}
      <div className="flex items-start gap-4 mb-5">
        <img src={creator.avatar} alt={creator.name} className="w-[52px] h-[52px] rounded-full object-cover shrink-0 border border-gray-100 shadow-sm" />
        <div className="flex-1 min-w-0 pt-0.5">
          <h3 className="text-[17px] font-bold text-gray-900 truncate group-hover:text-blue-600 transition-colors">{creator.name}</h3>
          <p className="text-xs text-gray-500 truncate mb-1.5 opacity-80">@{creator.handle}</p>
          <div className="flex items-center gap-3 text-xs text-gray-500">
             <span className="flex items-baseline gap-1"><span className="text-sm font-bold text-gray-900">{creator.followers}</span> 粉丝</span>
             <span className="flex items-baseline gap-1"><span className="text-sm font-bold text-gray-900">{creator.likes || '0'}</span> 获赞</span>
          </div>
        </div>
      </div>
      
      {/* Tags */}
      <div className="flex flex-wrap gap-1.5 mb-5 h-[26px] overflow-hidden">
        {creator.tags && creator.tags.length > 0 ? (
          creator.tags.slice(0, 3).map((tag, i) => (
             <span key={i} className="px-2.5 py-1 bg-gray-50/80 border border-gray-100 text-gray-600 rounded-lg text-[11px] font-medium truncate max-w-[85px] transition-colors group-hover:bg-gray-100">
               {tag}
             </span>
          ))
        ) : (
          <span className="px-2.5 py-1 text-transparent rounded-lg text-[11px] select-none">无标签</span>
        )}
      </div>

      {/* Pinned Works */}
      <div className="flex gap-2 h-[130px]">
         {creator.pinnedWorks && creator.pinnedWorks.length > 0 ? (
           creator.pinnedWorks.slice(0, 2).map((work) => (
             <div
               key={work.id}
               className="relative flex-1 rounded-2xl overflow-hidden bg-gray-100 group/work isolate cursor-pointer"
               onClick={(e) => {
                 if (onWorkClick) {
                   e.preventDefault();
                   e.stopPropagation();
                   onWorkClick(work.id, creator);
                 }
               }}
             >
                <img
                  src={work.image}
                  alt={work.title}
                  className="h-full w-full rounded-[inherit] object-cover transition-transform duration-700 ease-out will-change-transform [backface-visibility:hidden] group-hover/work:scale-110"
                />
                {work.type === 'video' && (
                  <div className="absolute top-2 right-2 w-7 h-7 bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center border border-white/10">
                    <svg className="w-3.5 h-3.5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                  </div>
                )}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
                <div className="pointer-events-none absolute bottom-2.5 left-3 right-3 z-10 line-clamp-1 text-[11px] font-medium leading-none text-white drop-shadow-sm">
                  {work.title}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover/work:opacity-100 transition-opacity duration-500" />
             </div>
           ))
         ) : (
           <div className="w-full h-full rounded-2xl bg-gray-50 flex items-center justify-center text-xs text-gray-400 border border-dashed border-gray-200">
             暂无作品
           </div>
         )}
         {creator.pinnedWorks?.length === 1 && (
            <div className="flex-1 rounded-2xl bg-gray-50/50 border border-dashed border-gray-200" />
         )}
      </div>
    </Link>
  )
}

const AuthorsSection = ({ creators, loading, onWorkClick }: { creators: Creator[], loading: boolean, onWorkClick?: (workId: string, creator: Creator) => void }) => {
  return (
    <section className="pt-6 sm:pt-10 pb-10 sm:pb-16 px-3 sm:px-4 md:px-6 max-w-[1504px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 sm:mb-12 px-1 sm:px-2">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium text-gray-900 tracking-tight">
          创作者
        </h2>
        <Link to="/discover?tab=creators" className="hidden md:flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-black mt-4 md:mt-0 transition-colors">
          浏览更多 <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {loading ? (
          Array.from({ length: 8 }).map((_, index) => (
             <div key={`creator-skeleton-${index}`} className="animate-pulse bg-white rounded-[2rem] border border-gray-100 p-6">
               <div className="flex gap-4 mb-5">
                 <div className="w-[52px] h-[52px] bg-gray-100 rounded-full shrink-0" />
                 <div className="flex-1 pt-1">
                   <div className="h-4 bg-gray-100 w-1/2 rounded mb-2.5" />
                   <div className="h-3 bg-gray-100 w-1/3 rounded mb-3.5" />
                   <div className="h-3 bg-gray-100 w-2/3 rounded" />
                 </div>
               </div>
               <div className="flex gap-2 mb-5">
                 <div className="h-[26px] w-16 bg-gray-100 rounded-lg" />
                 <div className="h-[26px] w-16 bg-gray-100 rounded-lg" />
               </div>
               <div className="flex gap-2 h-[130px]">
                 <div className="flex-1 bg-gray-100 rounded-2xl" />
                 <div className="flex-1 bg-gray-100 rounded-2xl" />
               </div>
             </div>
          ))
        ) : creators.length === 0 ? (
          <div className="col-span-full rounded-3xl border border-dashed border-gray-200 py-20 text-center text-sm text-gray-400">
             暂无创作者
          </div>
        ) : (
          creators.map(creator => (
            <AuthorCard key={creator.id} creator={creator} onWorkClick={onWorkClick} />
          ))
        )}
      </div>
    </section>
  );
};

type HomeBriefTab = 'latest' | 'hot';

const HOME_BRIEF_LIMIT = 8;

const getBriefTimestamp = (brief: DiscoverBrief) => {
  const raw = brief.createTime || brief.deadlineDate || brief.deadline || '';
  const time = new Date(raw).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const getHomeBriefScore = (brief: DiscoverBrief) => {
  const communication = Math.log1p(brief.communicationCount ?? 0);
  const views = Math.log1p(brief.viewCount ?? 0);
  return 0.9 * communication + 0.1 * views;
};

const sortHomeBriefs = (briefs: DiscoverBrief[], tab: HomeBriefTab) => {
  return [...briefs].sort((a, b) => {
    if (tab === 'latest') {
      const timeDiff = getBriefTimestamp(b) - getBriefTimestamp(a);
      if (timeDiff !== 0) return timeDiff;
      return String(b.id).localeCompare(String(a.id));
    }

    const scoreDiff = getHomeBriefScore(b) - getHomeBriefScore(a);
    if (Math.abs(scoreDiff) > 1e-6) return scoreDiff;
    const timeDiff = getBriefTimestamp(b) - getBriefTimestamp(a);
    if (timeDiff !== 0) return timeDiff;
    return String(b.id).localeCompare(String(a.id));
  });
};

const BriefList = ({
  onBriefClick,
  activeTab,
  onTabChange,
  briefsByTab,
  loading
}: {
  onBriefClick: (brief: DiscoverBrief) => void;
  activeTab: HomeBriefTab;
  onTabChange: (tab: HomeBriefTab) => void;
  briefsByTab: { latest: DiscoverBrief[]; hot: DiscoverBrief[] };
  loading: boolean;
}) => {
  const visibleBriefs = useMemo(() => sortHomeBriefs(briefsByTab[activeTab], activeTab), [activeTab, briefsByTab]);

  return (
    <section className="py-6 sm:py-10 px-3 sm:px-6 md:px-12 max-w-[1440px] mx-auto mb-12 sm:mb-20">
      <div className="bg-[#F0F4F9] rounded-[2rem] sm:rounded-[3rem] p-5 sm:p-8 md:p-16">
        <div className="flex flex-col gap-4 sm:gap-6 md:flex-row md:items-end md:justify-between mb-6 sm:mb-12">
          <div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-medium text-gray-900 tracking-tight">需求</h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="inline-flex rounded-full bg-white p-1 shadow-sm border border-gray-200">
              <button
                type="button"
                onClick={() => onTabChange('latest')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${activeTab === 'latest' ? 'bg-black text-white shadow-sm' : 'text-gray-600 hover:text-black'}`}
              >
                最新需求
              </button>
              <button
                type="button"
                onClick={() => onTabChange('hot')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${activeTab === 'hot' ? 'bg-black text-white shadow-sm' : 'text-gray-600 hover:text-black'}`}
              >
                最热需求
              </button>
            </div>
            <Link
              to="/discover?tab=briefs"
              className="w-12 h-12 rounded-full border border-gray-300 flex items-center justify-center hover:bg-black hover:border-black hover:text-white transition-all shrink-0"
            >
              <ArrowUpRight className="w-5 h-5" />
            </Link>
          </div>
        </div>

        <div className="space-y-4 max-h-[680px] overflow-y-auto pr-2 no-scrollbar">
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div
                key={`brief-skeleton-${index}`}
                className="p-6 md:p-8 bg-white rounded-[2rem] border border-gray-100 animate-pulse"
              >
                <div className="h-4 w-28 bg-gray-100 rounded-full mb-4" />
                <div className="h-7 w-3/4 bg-gray-100 rounded-full mb-6" />
                <div className="flex flex-wrap gap-4">
                  <div className="h-4 w-24 bg-gray-100 rounded-full" />
                  <div className="h-4 w-24 bg-gray-100 rounded-full" />
                  <div className="h-4 w-24 bg-gray-100 rounded-full" />
                </div>
              </div>
            ))
          ) : visibleBriefs.length === 0 ? (
            <div className="p-10 bg-white rounded-[2rem] text-center text-gray-400">
              暂无需求
            </div>
          ) : (
            visibleBriefs.map((brief) => (
              <BriefCard
                key={brief.id}
                brief={{
                  ...brief,
                  userId: brief.clientUserId,
                  budgetText: brief.budget,
                  deadlineText: brief.deadline || '可谈',
                  durationText: brief.expectedDurationDays ? `${brief.expectedDurationDays}天` : '可谈'
                } satisfies BriefCardData}
                onClick={() => onBriefClick(brief)}
                variant="white"
              />
            ))
          )}
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const [briefTab, setBriefTab] = useState<HomeBriefTab>('latest');
  const [selectedBrief, setSelectedBrief] = useState<BriefDetailType | null>(null);
  const [selectedWork, setSelectedWork] = useState<WorkDetail | null>(null);

  const { data: briefsData } = useSWR(
    'home-briefs',
    async () => {
      const [latest, hot] = await Promise.all([
        discoverService.getBriefs({ sort: 'latest', limit: HOME_BRIEF_LIMIT }),
        discoverService.getBriefs({ sort: 'hot', limit: HOME_BRIEF_LIMIT }),
      ]);
      return { latest, hot };
    },
    { revalidateOnFocus: false, dedupingInterval: 60000 }
  );

  const { data: recommendedWorks } = useSWR(
    'home-recommended-works',
    () => postService.getRecommendedPosts(30),
    { revalidateOnFocus: false, dedupingInterval: 60000 }
  );

  const { data: hotWorks } = useSWR(
    'home-hot-works',
    () => postService.getHotPosts(30),
    { revalidateOnFocus: false, dedupingInterval: 60000 }
  );

  const { data: featuredWorks } = useSWR(
    'home-featured-works',
    () => postService.getFeaturedPosts(),
    { revalidateOnFocus: false, dedupingInterval: 60000 }
  );

  const { data: topCreators } = useSWR(
    'home-top-creators',
    () => discoverService.getTopCreators({ limit: 12, minReviews: 0 }),
    { revalidateOnFocus: false, dedupingInterval: 60000 }
  );

  const { data: heroCreators } = useSWR(
    'home-hero-creators-by-fans',
    getHeroCreators,
    { revalidateOnFocus: false, dedupingInterval: 60000 }
  );

  const latestBriefs = briefsData?.latest || [];
  const hotBriefs = briefsData?.hot || [];
  const briefsLoading = !briefsData;

  // Merge featured + hot + recommended (featured first), deduped by id.
  // The hot-posts cache filters out anything with zero engagement signals
  // and applies a per-creator diversity cap, so on a young platform it's
  // typically much smaller than the active inventory. Including the
  // recommended feed as a tail keeps the section full once admin-curated
  // and engagement-ranked sources run dry.
  const mergedWorks = useMemo(() => {
    const featured = featuredWorks || [];
    const hot = hotWorks || [];
    const recommended = recommendedWorks || [];
    const seen = new Set<string>();
    const out: WorkSummary[] = [];
    for (const w of [...featured, ...hot, ...recommended]) {
      if (w.id && !seen.has(w.id)) { seen.add(w.id); out.push(w); }
    }
    return out;
  }, [featuredWorks, hotWorks, recommendedWorks]);

  const handleBriefClick = (brief: DiscoverBrief) => {
    const fullBrief: BriefDetailType = {
      id: brief.id,
      title: brief.title,
      description: brief.description || "这是一个激动人心的项目，我们需要顶尖的创意人才来共同完成。如果您对AIGC有深刻的理解，并且有相关的作品集，请务必联系我们。",
      deliverables: brief.deliverables || "1. 30秒高品质视频\n2. 原始工程文件\n3. 相关的静态海报生成",
      budgetRange: brief.budget,
      deliveryTimeRange: brief.expectedDurationDays ? `${brief.expectedDurationDays}天` : '可谈',
      communicationCount: brief.communicationCount ?? 0,
      trialCount: brief.trialCount ?? 0,
      viewCount: brief.viewCount ?? 0,
      tags: brief.tags || [],
      exampleWorks: [],
      targetDate: brief.deadlineDate || ''
    };
    setSelectedBrief(fullBrief);
  };

  const handleWorkClick = (work: WorkSummary) => {
    const fullWork: WorkDetail = {
      id: work.id,
      type: work.type || 'image',
      title: work.title,
      description: work.description,
      thumbnailUrl: work.thumbnailUrl,
      videoUrl: work.videoUrl,
      images: work.images || (work.type === 'image' && work.thumbnailUrl ? [work.thumbnailUrl] : undefined),
      tags: work.tags || [],
      likes: work.likes,
      views: work.views,
      favorites: work.favorites,
      comments: [],
      createdAt: work.createdAt,
      author: {
        id: work.creator?.id || work.creatorUserId,
        name: work.creator?.name || work.creatorUserId,
        avatar: work.creator?.avatar || DEFAULT_AVATAR_URL
      },
      isPinned: work.isPinned,
      liked: work.liked,
      bookmarked: work.bookmarked,
      auditStatus: work.auditStatus,
      visibility: work.visibility,
      status: work.status
    };
    setSelectedWork(fullWork);
  };

  const handleAuthorWorkClick = async (workId: string) => {
    const detail = await postService.getPostDetail(String(workId));
    if (!detail) {
      console.error('Failed to load author work detail:', workId);
      return;
    }

    setSelectedWork(detail);
  };

  return (
    <div className="font-sans text-gray-900 bg-white min-h-screen selection:bg-blue-100">
      <HeroSection creators={heroCreators || []} loading={!heroCreators} />
      <RecommendedWorksSection
        works={mergedWorks}
        loading={!featuredWorks && !hotWorks && !recommendedWorks}
        onWorkClick={handleWorkClick}
      />
      <AuthorsSection
        creators={topCreators || []}
        loading={!topCreators}
        onWorkClick={handleAuthorWorkClick}
      />
      <BriefList
        onBriefClick={handleBriefClick}
        activeTab={briefTab}
        onTabChange={setBriefTab}
        briefsByTab={{ latest: latestBriefs, hot: hotBriefs }}
        loading={briefsLoading}
      />

      {/* Modals */}
      {selectedBrief && (
        <BriefDetailModal
          isOpen={!!selectedBrief}
          onClose={() => setSelectedBrief(null)}
          brief={selectedBrief}
          isOwnProfile={false} // Viewing others' briefs
        />
      )}

      {selectedWork && (
        <WorkDetailModal
          isOpen={!!selectedWork}
          onClose={() => setSelectedWork(null)}
          work={selectedWork}
          isOwnProfile={false} // Viewing others' works
        />
      )}
    </div>
  );
};

export default Home;
